import { useSnackbarController, useTranslation } from "@firecms/core";
import { Button, CircularProgress } from "@firecms/ui";
import { useCallback, useMemo, useState } from "react";
import { httpsCallable } from "firebase/functions";
import { useFirebaseFunctions } from "../hooks/useFirebaseFunctions";
import { getModeCollectionIds } from "../workspace/adminMode";
import { useAdminWorkspace } from "../workspace/useAdminWorkspace";
import { toCallableWorkspaceParams } from "../workspace/callableParams";
import {
  AdminModal,
  adminModalInputClass,
  adminModalLabelClass,
} from "../custom/AdminModal";
import { useAdminTranslation } from "../i18n/useAdminTranslation";

type AddTeamsResponse = {
  added: number;
  teamSlotCount: number;
};

function getCallableErrorMessage(error: unknown, fallback: string): string {
  if (!(error instanceof Error)) {
    return fallback;
  }
  const code =
    "code" in error && typeof error.code === "string" ? error.code : "";
  if (code.startsWith("functions/") && error.message) {
    return error.message;
  }
  return error.message || fallback;
}

export function AddTeamsButton() {
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState("1");
  const [loading, setLoading] = useState(false);
  const { mode } = useAdminWorkspace();
  const workspaceIds = useMemo(() => getModeCollectionIds(mode), [mode]);
  const functions = useFirebaseFunctions();
  const snackbarController = useSnackbarController();
  const { t } = useTranslation();
  const { t: ta } = useAdminTranslation();

  const eventReady = Boolean(workspaceIds.event?.eventId);

  const handleClose = useCallback(() => {
    if (!loading) {
      setOpen(false);
      setCount("1");
    }
  }, [loading]);

  const handleSubmit = useCallback(async () => {
    if (!functions || !eventReady) {
      snackbarController.open({
        message: ta("common.selectActiveEvent"),
        type: "warning",
      });
      return;
    }

    const parsed = Number(count);
    if (!Number.isInteger(parsed) || parsed < 1) {
      snackbarController.open({
        message: ta("addTeams.invalidCount"),
        type: "warning",
      });
      return;
    }

    const callable = httpsCallable<
      Record<string, unknown>,
      AddTeamsResponse
    >(functions, "addTeams");

    try {
      setLoading(true);
      const result = await callable({
        ...toCallableWorkspaceParams(workspaceIds),
        count: parsed,
      });
      const { added, teamSlotCount } = result.data;
      snackbarController.open({
        message: ta("addTeams.success", { added, teamSlotCount }),
        type: "success",
      });
      handleClose();
    } catch (error: unknown) {
      const message = getCallableErrorMessage(error, ta("common.unknownError"));
      snackbarController.open({
        message: ta("addTeams.error", { message }),
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [
    count,
    eventReady,
    functions,
    handleClose,
    snackbarController,
    ta,
    workspaceIds,
  ]);

  return (
    <>
      <Button
        onClick={() => setOpen(true)}
        variant="outlined"
        disabled={!eventReady}
      >
        {ta("addTeams.button")}
      </Button>

      <AdminModal
        open={open}
        onClose={handleClose}
        ariaLabel={ta("addTeams.ariaLabel")}
        title={ta("addTeams.title")}
        description={ta("addTeams.description")}
        footer={
          <>
            {loading && <CircularProgress size="small" />}
            <Button onClick={handleClose} disabled={loading} variant="outlined">
              {t("cancel")}
            </Button>
            <Button
              disabled={loading || !count.trim()}
              onClick={() => void handleSubmit()}
              variant="outlined"
            >
              {ta("addTeams.confirm")}
            </Button>
          </>
        }
      >
        <label className={adminModalLabelClass}>{ta("addTeams.countLabel")}</label>
        <input
          type="number"
          min={1}
          className={adminModalInputClass}
          value={count}
          onChange={(e) => setCount(e.target.value)}
          disabled={loading}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              void handleSubmit();
            }
          }}
        />
      </AdminModal>
    </>
  );
}
